import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Role } from './models';
import { baseApi } from '@centerpiece/components';

export const role = baseApi
  .enhanceEndpoints({ addTagTypes: ['Roles', 'Role'] })
  .injectEndpoints({
    endpoints: builder => ({
      getRoles: builder.query<Role[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'role',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Role' as const, id })),
                'Roles',
              ]
            : ['Roles'],
      }),
      getRole: builder.query<Role, string>({
        query: id => ({
          url: `role/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Role', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createRole: builder.mutation({
        query: request => ({
          url: 'role',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Roles'],
      }),
      updateRole: builder.mutation<Role, Role>({
        query: request => ({
          url: `role/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [{ type: 'Role', id: arg.id }],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            role.util.updateQueryData('getRole', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(role.util.invalidateTags(['Roles']))
             */
            dispatch(role.util.invalidateTags(['Roles']));
          }
        },
      }),
      deleteRole: builder.mutation<Role, number>({
        query: id => ({
          url: `role/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Role' as const, arg }, 'Roles'] : ['Roles'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  usePrefetch: useRolesPrefetch,
} = role;
const selectRolesRoot = role.endpoints.getRoles.select({});
export const selectRoles = createSelector(
  selectRolesRoot,
  role => role.data || []
);
